import React, { useEffect, useState, useCallback, useContext } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Typography,
  Spin,
  message,
  DatePicker,
} from 'antd';
import {
  DollarCircleOutlined,
  ShoppingCartOutlined,
  LineChartOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';
import dayjs from 'dayjs';
import { AuthContext } from '../../context/AuthContext';
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ✅ Currency & number formatting
const formatCurrency = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

const formatNumber = (val) => new Intl.NumberFormat('en-US').format(val || 0);

// ✅ Reusable Statistic Card Component
const StatCard = ({ title, value, prefix, suffix, precision, loading, color }) => (
  <Card
    style={{
      borderRadius: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    }}
  >
    <Spin spinning={loading}>
      <Statistic
        title={<Text strong>{title}</Text>}
        value={value}
        precision={precision}
        prefix={prefix}
        suffix={suffix}
        valueStyle={{ color: color || '#333', fontWeight: 600 }}
      />
    </Spin>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);
  const [messageApi, contextHolder] = message.useMessage();
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.id === 1; // 👈 ONLY THIS USER

  // ✅ Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('startDate', dateRange[0].toISOString());
        params.append('endDate', dateRange[1].toISOString());
      }

      const { data } = await api.get(`/api/dashboard/stats?${params.toString()}`);
      setStats(data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      messageApi.error('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, messageApi]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // ✅ Table columns
  const topProductsColumns = [
    { title: 'Part Name', dataIndex: 'partName', key: 'partName' },
    {
      title: 'Quantity Sold',
      dataIndex: 'totalQuantitySold',
      key: 'quantity',
      align: 'right',
      render: formatNumber,
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'revenue',
      align: 'right',
      render: (val) => formatCurrency(val),
    },
  ];

  const topCustomersColumns = [
    { title: 'Customer Name', dataIndex: 'name', key: 'name' },
    { title: 'TIN', dataIndex: 'tin', key: 'tin' },
    {
      title: 'Total Spent',
      dataIndex: 'totalSpent',
      key: 'spent',
      align: 'right',
      render: (val) => formatCurrency(val),
    },
  ];

  const salesByShopColumns = [
    { title: 'Shop Name', dataIndex: 'shopName', key: 'shop' },
    {
      title: 'Total Sales',
      dataIndex: 'totalSales',
      key: 'sales',
      align: 'right',
      render: (val) => formatCurrency(val),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px', minHeight: '100vh' }}>
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Admin Dashboard
            </Title>
            <Text type="secondary">Comprehensive overview of daily performance</Text>
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              allowClear={false}
              style={{ borderRadius: 6 }}
            />
          </Col>
        </Row>

        {isSuperAdmin && (
          <>
            {/* KPI Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={8}>
                <StatCard
                  title="Total Sales"
                  value={formatCurrency(stats?.kpis?.totalSales || 0)}
                  prefix={<DollarCircleOutlined style={{ color: '#52c41a' }} />}
                  color="#52c41a"
                  loading={loading}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <StatCard
                  title="Total Orders"
                  value={formatNumber(stats?.kpis?.totalOrders || 0)}
                  prefix={<ShoppingCartOutlined style={{ color: '#1890ff' }} />}
                  color="#1890ff"
                  loading={loading}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <StatCard
                  title="Avg. Order Value"
                  value={formatCurrency(stats?.kpis?.averageOrderValue || 0)}
                  prefix={<LineChartOutlined style={{ color: '#faad14' }} />}
                  color="#faad14"
                  loading={loading}
                />
              </Col>
            </Row>

            {/* Tables */}
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card
                  title="Top Selling Products"
                  bordered={false}
                  style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  <Table
                    loading={loading}
                    columns={topProductsColumns}
                    dataSource={stats?.topSellingProducts || []}
                    pagination={false}
                    rowKey="sparepartId"
                    size="small"
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  title="Top Customers"
                  bordered={false}
                  style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  <Table
                    loading={loading}
                    columns={topCustomersColumns}
                    dataSource={stats?.topCustomers || []}
                    pagination={false}
                    rowKey="customerId"
                    size="small"
                  />
                </Card>
              </Col>
              <Col xs={24}>
                <Card
                  title="Sales by Shop"
                  bordered={false}
                  style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  <Table
                    loading={loading}
                    columns={salesByShopColumns}
                    dataSource={stats?.salesByShop || []}
                    pagination={false}
                    rowKey={(record) => record['shop.name']}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default AdminDashboard;